/**
 * API-based Search Client
 *
 * Uses API Gateway proxy endpoints instead of direct Typesense connection
 * to solve HTTPS/CORS issues.
 */

import { getIdToken } from '@/lib/api/auth';
import { getApiUrl } from '@/lib/api/core';

const API_BASE_URL = getApiUrl();

interface SearchResponse<T> {
  hits: T[];
  found: number;
  page: number;
  facets?: Record<string, any>;
}

interface EnvelopedSearchResponse<T> {
  data: SearchResponse<T>;
}

/**
 * Search customers via API proxy
 */
export async function searchCustomers(params: {
  q?: string;
  page?: number;
  per_page?: number;
  city?: string;
  cities?: string[];
  serviceTemplates?: string | string[]; // Single template or array of templates
  paymentStatus?: string; // 'missing' | 'expired' | 'expiring' | 'issues' | 'valid'
  sort_by?: string;
  hasWelcomeEmail?: boolean;
  hasEmail?: boolean;
  status?: string;
  hasAnyContact?: boolean;
  hasAnyEmail?: boolean;
  hasAnyPhone?: boolean;
  /** true = only customers who replied STOP, whom no text reaches. There is no inverse. */
  smsOptedOut?: boolean;
  balanceFilter?: string;
  /** When true, only customers with activeServiceCount > 0. */
  hasActiveService?: boolean;
  /** When true, drop deactivated "do not contact" customers (RUT-3075) — marketing/outreach only. */
  excludeDeactivated?: boolean;
  /**
   * Export every matching doc (no search-page ceiling). Pair with
   * fields: 'audience' for campaign lists or 'minimal' for map markers.
   */
  fetchAll?: boolean;
  /** 'minimal' | 'audience' — shapes the export payload when fetchAll is set. */
  fields?: 'minimal' | 'audience';
}) {
  const token = getIdToken();

  const queryParams = new URLSearchParams();
  if (params.q) queryParams.append('q', params.q);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page !== undefined) queryParams.append('per_page', params.per_page.toString());
  if (params.cities && params.cities.length > 0) {
    queryParams.append('cities', params.cities.join(','));
  } else if (params.city) {
    queryParams.append('city', params.city);
  }

  // Support both single string and array of service template IDs
  if (params.serviceTemplates) {
    const ids = Array.isArray(params.serviceTemplates)
      ? params.serviceTemplates
      : [params.serviceTemplates];
    if (ids.length > 0) {
      queryParams.append('serviceTemplates', ids.join(','));
    }
  }

  if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);
  if (params.hasWelcomeEmail !== undefined) queryParams.append('hasWelcomeEmail', params.hasWelcomeEmail.toString());
  if (params.hasEmail !== undefined) queryParams.append('hasEmail', params.hasEmail.toString());
  if (params.status) queryParams.append('status', params.status);
  if (params.hasAnyContact !== undefined) queryParams.append('hasAnyContact', params.hasAnyContact.toString());
  if (params.hasAnyEmail !== undefined) queryParams.append('hasAnyEmail', params.hasAnyEmail.toString());
  if (params.hasAnyPhone !== undefined) queryParams.append('hasAnyPhone', params.hasAnyPhone.toString());
  if (params.smsOptedOut !== undefined) queryParams.append('smsOptedOut', params.smsOptedOut.toString());
  if (params.balanceFilter) queryParams.append('balanceFilter', params.balanceFilter);
  if (params.hasActiveService !== undefined) {
    queryParams.append('hasActiveService', params.hasActiveService.toString());
  }
  if (params.excludeDeactivated) queryParams.append('excludeDeactivated', 'true');
  if (params.fetchAll) queryParams.append('fetchAll', 'true');
  if (params.fields) queryParams.append('fields', params.fields);

  const response = await fetch(`${API_BASE_URL}/search?type=customers&${queryParams}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText}`);
  }

  const envelope = (await response.json()) as EnvelopedSearchResponse<any>;
  return envelope.data;
}

/**
 * Search subscriptions via API proxy
 */
export async function searchSubscriptions(params: {
  q?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  serviceTemplateId?: string;
  serviceTemplateIds?: string[];
  serviceTemplateCategory?: string;
  status?: string;
  assignedCrewId?: string;
  assignedCrewIds?: string[];
  rateVisibility?: string;
  pricingLocked?: string;
  isReviewed?: string;
  rateRequested?: boolean;
  rateReviewFlagged?: boolean; // Services flagged from a finished visit for a later rate review
  excludeSnoozed?: boolean; // Drop actively-snoozed rate requests (RUT-2698) — used by the pending metric
  awaitingResponse?: boolean;
  sentRates?: boolean; // All rates sent to customers — both awaiting and responded
  sentBefore?: number; // Unix seconds — only rates sent before this cutoff (age filter for awaitingResponse)
  customerCity?: string;
  customerCities?: string[];
  rateMismatchType?: string;
  overchargeThreshold?: number;
  underchargeThreshold?: number;
  hasMetrics?: boolean;
  hasSchedule?: string;
  selectedPeriodName?: string;
  selectedPeriodNames?: string[];
  customerSelectedOptionState?: string;
  customerSelectedOptionStates?: string[];
  isOneOffService?: boolean;
  hasOutstandingCustomerQuestions?: boolean;
  isMissingSeason?: boolean;
  /** With status=active, also return services held until a future start (RUT-4118). */
  includePendingStart?: boolean;
}) {
  const token = getIdToken();

  const queryParams = new URLSearchParams();
  if (params.q) queryParams.append('q', params.q);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);

  // Support both single and multi-select for service templates
  if (params.serviceTemplateIds && params.serviceTemplateIds.length > 0) {
    queryParams.append('serviceTemplateIds', params.serviceTemplateIds.join(','));
  } else if (params.serviceTemplateId) {
    queryParams.append('serviceTemplateId', params.serviceTemplateId);
  }

  // Service template category filter
  if (params.serviceTemplateCategory) {
    queryParams.append('serviceTemplateCategory', params.serviceTemplateCategory);
  }

  if (params.status && params.status !== 'all') queryParams.append('status', params.status);
  if (params.assignedCrewIds && params.assignedCrewIds.length > 0) {
    queryParams.append('assignedCrewIds', params.assignedCrewIds.join(','));
  } else if (params.assignedCrewId) {
    queryParams.append('assignedCrewId', params.assignedCrewId);
  }
  if (params.rateVisibility) queryParams.append('rateVisibility', params.rateVisibility);

  // Support both single and multi-select for cities
  if (params.customerCities && params.customerCities.length > 0) {
    queryParams.append('customerCities', params.customerCities.join(','));
  } else if (params.customerCity) {
    queryParams.append('customerCity', params.customerCity);
  }

  if (params.rateMismatchType) queryParams.append('rateMismatchType', params.rateMismatchType);

  // New threshold filters
  if (params.overchargeThreshold !== undefined) {
    queryParams.append('overchargeThreshold', params.overchargeThreshold.toString());
  }
  if (params.underchargeThreshold !== undefined) {
    queryParams.append('underchargeThreshold', params.underchargeThreshold.toString());
  }

  if (params.hasMetrics !== undefined) queryParams.append('hasMetrics', params.hasMetrics.toString());
  if (params.hasSchedule && params.hasSchedule !== 'all') queryParams.append('hasSchedule', params.hasSchedule);
  if (params.pricingLocked) queryParams.append('pricingLocked', params.pricingLocked);
  if (params.isReviewed) queryParams.append('isReviewed', params.isReviewed);
  if (params.rateRequested !== undefined) queryParams.append('rateRequested', params.rateRequested.toString());
  if (params.rateReviewFlagged !== undefined) queryParams.append('rateReviewFlagged', params.rateReviewFlagged.toString());
  if (params.excludeSnoozed !== undefined) queryParams.append('excludeSnoozed', params.excludeSnoozed.toString());
  if (params.awaitingResponse !== undefined) queryParams.append('awaitingResponse', params.awaitingResponse.toString());
  if (params.sentRates !== undefined) queryParams.append('sentRates', params.sentRates.toString());
  if (params.sentBefore !== undefined) queryParams.append('sentBefore', params.sentBefore.toString());
  if (params.selectedPeriodNames && params.selectedPeriodNames.length > 0) {
    queryParams.append('selectedPeriodNames', params.selectedPeriodNames.join(','));
  } else if (params.selectedPeriodName) {
    queryParams.append('selectedPeriodName', params.selectedPeriodName);
  }
  if (params.customerSelectedOptionStates && params.customerSelectedOptionStates.length > 0) {
    queryParams.append('customerSelectedOptionStates', params.customerSelectedOptionStates.join(','));
  } else if (params.customerSelectedOptionState) {
    queryParams.append('customerSelectedOptionState', params.customerSelectedOptionState);
  }
  if (params.isOneOffService !== undefined) queryParams.append('isOneOffService', params.isOneOffService.toString());
  if (params.hasOutstandingCustomerQuestions) queryParams.append('hasOutstandingCustomerQuestions', 'true');
  if (params.isMissingSeason) queryParams.append('isMissingSeason', 'true');
  if (params.includePendingStart) queryParams.append('includePendingStart', 'true');

  const response = await fetch(`${API_BASE_URL}/search?type=subscriptions&${queryParams}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText}`);
  }

  const envelope = (await response.json()) as EnvelopedSearchResponse<any>;
  return envelope.data;
}

/**
 * Page through every hit for a subscription search (RUT-3185 bulk pricing).
 * Uses the same page/`found` model as the API proxy rather than cursor tokens.
 */
export async function searchAllSubscriptions(
  params: Parameters<typeof searchSubscriptions>[0],
  perPage = 250,
): Promise<{ hits: any[]; found: number }> {
  const first = await searchSubscriptions({ ...params, page: 1, per_page: perPage });
  const hits = [...(first.hits || [])];
  const found = typeof first.found === 'number' ? first.found : hits.length;
  const totalPages = Math.max(1, Math.ceil(found / perPage));

  if (totalPages <= 1) return { hits, found };

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      searchSubscriptions({ ...params, page: i + 2, per_page: perPage }),
    ),
  );
  for (const page of rest) {
    hits.push(...(page.hits || []));
  }
  return { hits, found };
}

/**
 * Search visits via API proxy
 */
export async function searchVisits(params: {
  q?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  status?: string;
  statuses?: string[];
  crewId?: string;
  scheduledDateStart?: number;
  scheduledDateEnd?: number;
  completedDateStart?: number;
  completedDateEnd?: number;
  serviceName?: string;
  serviceNames?: string[];
  reviewRequired?: string;
  /** 'true' — only visits with the completion bell armed (RUT-2933). */
  notifyAdminOnCompletion?: string;
  hasPhotos?: boolean;
  hasNotes?: boolean;
  isFollowupVisit?: boolean;
  billingMethod?: string;
  customerId?: string;
  customerCity?: string;
  serviceTemplateId?: string;
  taxRatePercentHundredths?: number;
  priority?: boolean;
  dispatchJobId?: string;
  /** Schedule → Today live-day scope (RUT-3334). Unix seconds; both required. */
  liveDayStart?: number;
  liveDayEnd?: number;
  /** Money words for the Payment filter (RUT-3640). */
  paymentFacets?: string[];
  /** Leave assessment visits out — Today's Sales tile links (RUT-4440). */
  excludeAssessments?: boolean;
}) {
  const token = getIdToken();

  const queryParams = new URLSearchParams();
  if (params.q) queryParams.append('q', params.q);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);

  if (params.statuses && params.statuses.length > 0) {
    queryParams.append('statuses', params.statuses.join(','));
  } else if (params.status) {
    queryParams.append('status', params.status);
  }

  if (params.crewId) queryParams.append('crewId', params.crewId);
  if (params.scheduledDateStart) queryParams.append('scheduledDateStart', params.scheduledDateStart.toString());
  if (params.scheduledDateEnd) queryParams.append('scheduledDateEnd', params.scheduledDateEnd.toString());
  if (params.completedDateStart) queryParams.append('completedDateStart', params.completedDateStart.toString());
  if (params.completedDateEnd) queryParams.append('completedDateEnd', params.completedDateEnd.toString());
  if (params.serviceNames && params.serviceNames.length > 0) {
    // Repeat the key so a name that contains a comma is not split (RUT-3462).
    for (const name of params.serviceNames) {
      if (name) queryParams.append('serviceNames', name);
    }
  } else if (params.serviceName) {
    queryParams.append('serviceName', params.serviceName);
  }
  if (params.reviewRequired) queryParams.append('reviewRequired', params.reviewRequired);
  if (params.notifyAdminOnCompletion) queryParams.append('notifyAdminOnCompletion', params.notifyAdminOnCompletion);
  if (params.hasPhotos !== undefined) queryParams.append('hasPhotos', params.hasPhotos.toString());
  if (params.hasNotes !== undefined) queryParams.append('hasNotes', params.hasNotes.toString());
  if (params.isFollowupVisit !== undefined) queryParams.append('isFollowupVisit', params.isFollowupVisit.toString());
  if (params.billingMethod) queryParams.append('billingMethod', params.billingMethod);
  if (params.customerId) queryParams.append('customerId', params.customerId);
  if (params.customerCity) queryParams.append('customerCity', params.customerCity);
  if (params.serviceTemplateId) queryParams.append('serviceTemplateId', params.serviceTemplateId);
  if (params.taxRatePercentHundredths !== undefined) {
    queryParams.append('taxRatePercentHundredths', params.taxRatePercentHundredths.toString());
  }
  if (params.priority !== undefined) queryParams.append('priority', params.priority.toString());
  if (params.dispatchJobId) queryParams.append('dispatchJobId', params.dispatchJobId);
  if (params.liveDayStart != null) queryParams.append('liveDayStart', params.liveDayStart.toString());
  if (params.liveDayEnd != null) queryParams.append('liveDayEnd', params.liveDayEnd.toString());
  if (params.paymentFacets && params.paymentFacets.length > 0) {
    queryParams.append('paymentFacets', params.paymentFacets.join(','));
  }
  if (params.excludeAssessments) queryParams.append('excludeAssessments', 'true');

  const response = await fetch(`${API_BASE_URL}/search?type=visits&${queryParams}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText}`);
  }

  const envelope = (await response.json()) as EnvelopedSearchResponse<any>;
  return envelope.data;
}

/**
 * Check if search is available
 */
export function isSearchAvailable(): boolean {
  return !!import.meta.env.VITE_API_URL;
}
